import { useCallback, useMemo, useRef, useState } from 'react';

import {
  useApolloClient,
  useFragment as useApolloFragment,
  useMutation,
  useQuery,
} from '@apollo/client/react';
import { Alert, Dialog, Stack } from '@mui/material';
import {
  type RentalComparable,
  readRentalComparables,
} from '@realadvisor/listings/rental-cma';

import { ConfirmationDialog } from '@modules/_shared/components/ConfirmationDialog';
import RaGrid from '@modules/_shared/components/RaGrid';
import {
  type ReportComponentProps,
  useReportContext,
} from '@modules/_shared/components/report-editor/ReportEditorContext';
import ScraperListingCard from '@modules/_shared/components/scraper-listing-card/ScraperListingCard';
import { SlideUpTransition } from '@modules/_shared/components/SlideDialog';
import {
  DrawerContainerProvider,
  NO_DRAWER_CONTAINER,
  useDrawerContainer,
} from '@modules/_shared/hooks/drawerContainerProvider';
import { useScopedSearchParams } from '@modules/_shared/utils/navigation';
import ComparableListingsSkeleton from '@modules/comparable-listings/ComparableListingsSkeleton';
import { useAutoTour } from '@modules/product-updates/tours/TourProvider';
import {
  RENTAL_CMA_COHORTS,
  RENTAL_CMA_LISTINGS,
  STEP_RENTAL_LISTINGS_FRAGMENT,
  UPDATE_RENTAL_COMPARABLES,
  UPDATE_RENTAL_COMPARABLES_SEARCH_PARAMS,
} from '@modules/reports/cma-reports/rentalQueries';
import { FooterActionsComponentWithoutForm } from '@modules/reports/shared/FooterActionsComponentWithoutForm';
import { useLocale } from '@shared/hooks/locale';

import ComparableListingsSettings from './ComparableListingsSettings';
import ListingComparableDetails from './ListingComparableDetails';
import { ListingsPicker } from './ListingsPicker';
import { ListingsSelectionSection } from './ListingsSelectionSection';
import { ListingsStepSkeleton } from './ListingsStepSkeleton';
import { RentalComparableListings } from './RentalComparableListings';
import {
  fetchRentalComparables,
  getRentalCohortComparables,
  getRentalCohortListings,
  getRentalCohortsSearch,
  getRentalCountryRows,
  getRentalCountryVariables,
} from './rentalComparablesSearch';
import { toggleRentalSelection } from './rentalListingSelection';
import { SelectableCard } from './SelectableCard';
import { SortableListingCard } from './SortableListingCard';

const RentalListings = (props: ReportComponentProps) => {
  const drawerContainer = useDrawerContainer();
  const { t, countryCode } = useLocale();
  const { setError } = useReportContext();
  const client = useApolloClient();
  const [searchParams, setSearchParams] = useScopedSearchParams('rental_cma');
  const [tab, setTab] = useState<'manual' | 'automatic'>('manual');
  const busy = useRef(false);
  // Plays when the release entry's campaign targets this user.
  useAutoTour('cma-rental-comparables');
  const [manualOpen, setManualOpen] = useState(false);
  const [refreshOpen, setRefreshOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState<RentalComparable[]>([]);
  const [details, setDetails] = useState<RentalComparable | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [manualFiltersSeeded, setManualFiltersSeeded] = useState(false);
  const [cohortsRequested, setCohortsRequested] = useState(false);
  const { data: report, complete } = useApolloFragment({
    fragment: STEP_RENTAL_LISTINGS_FRAGMENT,
    fragmentName: 'StepRentalListings',
    from: { __typename: 'cma_reports', id: props.reportId },
  });
  const property = report?.property;
  const { variables: cohortsVariables } = getRentalCohortsSearch(
    property,
    countryCode,
  );
  const missingSurface =
    property?.computed_surface == null || property.computed_surface <= 0;
  const selected = useMemo(
    () => readRentalComparables(report?.rental_comparables),
    [report?.rental_comparables],
  );
  const savedSearchParams = report?.rental_comparables_search_params;
  const closeManualSelection = () => {
    setSearchParams(new URLSearchParams());
    setManualFiltersSeeded(false);
    setManualOpen(false);
  };
  const {
    data: listingsData,
    loading: searching,
    error: searchError,
  } = useQuery(RENTAL_CMA_COHORTS, {
    skip: !cohortsRequested,
    notifyOnNetworkStatusChange: true,
    context: { clientName: 'scrapers' },
    variables: cohortsVariables,
  });
  const cohortListings = useMemo(
    () => getRentalCohortListings(listingsData),
    [listingsData],
  );
  const candidates = useMemo(
    () => getRentalCohortComparables(cohortListings),
    [cohortListings],
  );

  const { data: selectedData, error: selectedError } = useQuery(
    RENTAL_CMA_LISTINGS,
    {
      skip: selected.length === 0,
      context: { clientName: 'scrapers' },
      variables: {
        ...getRentalCountryVariables(countryCode, {
          id: { _in: selected.map(item => item.id) },
          offer_type: { _eq: 'rent' },
        }),
        limit: selected.length,
      },
    },
  );
  const metadata = useMemo(
    () =>
      new Map(
        [...getRentalCountryRows(selectedData), ...cohortListings].map(
          listing => [listing.id, listing],
        ),
      ),
    [selectedData, cohortListings],
  );
  const [save, { loading: saving }] = useMutation(UPDATE_RENTAL_COMPARABLES, {
    onError: setError,
  });
  const [saveSearchParams, { loading: savingSearchParams }] = useMutation(
    UPDATE_RENTAL_COMPARABLES_SEARCH_PARAMS,
    { onError: setError },
  );
  const saveSelection = async (next: RentalComparable[]) => {
    if (busy.current) {
      return false;
    }
    busy.current = true;
    try {
      const result = await save({
        variables: { id: props.reportId, rental_comparables: next },
        optimisticResponse: {
          update_cma_reports_by_pk: {
            __typename: 'cma_reports',
            id: props.reportId,
            rental_comparables: next,
            updated_at: new Date().toISOString(),
          },
        },
      });
      return (
        result.error == null && result.data?.update_cma_reports_by_pk != null
      );
    } finally {
      busy.current = false;
    }
  };

  const saveManualSearchParams = async () => {
    const rental_comparables_search_params = Object.fromEntries(
      Array.from(searchParams.entries()).filter(
        ([key]) =>
          key !== 'page' && key !== 'offset' && key !== 'pinned_listing_id',
      ),
    );

    await saveSearchParams({
      variables: { id: props.reportId, rental_comparables_search_params },
    });
  };

  const getInitialManualSearchParams = () => {
    if (
      savedSearchParams != null &&
      typeof savedSearchParams === 'object' &&
      !Array.isArray(savedSearchParams)
    ) {
      const params = new URLSearchParams(
        Object.entries(savedSearchParams).flatMap(([key, value]) =>
          typeof value === 'string' ? [[key, value]] : [],
        ),
      );

      if (params.get('where') != null) {
        return params;
      }
    }

    return new URLSearchParams({
      where: JSON.stringify({
        listing: {
          ...(property?.locality_place?.id && {
            locality_object: {
              id: { _in: [property.locality_place.id] },
            },
          }),
          ...(property?.property_type?.main_type && {
            property_main_type: {
              _in: [property.property_type.main_type],
            },
          }),
        },
      }),
    });
  };

  const generateComparable = async () => {
    setRefreshOpen(false);
    if (searching || saving || generating) {
      return;
    }
    setGenerating(true);
    setCohortsRequested(true);
    try {
      const freshCandidates = await fetchRentalComparables(
        client,
        cohortsVariables,
      );
      if (freshCandidates.length > 0) {
        await saveSelection(freshCandidates);
      }
    } catch (error) {
      setError(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setGenerating(false);
    }
  };

  const renderCard = useCallback(
    (
      item: RentalComparable,
      variant: 'horizontal' | 'vertical' = 'horizontal',
    ) => {
      const listing = metadata.get(item.id);
      return (
        <ScraperListingCard
          listing={{
            ...listing,
            property_type: listing?.property_type ?? '',
            computed_surface: item.surface,
            number_of_rooms: item.rooms,
          }}
          addressLabel={item.address || `#${item.id}`}
          rentalPrice={item}
          variant={variant}
          showPricePerSqm
          showDaysOnMarket
          showOfflineSince
          dateBadgePosition="inline"
          onClick={() => setDetails(item)}
        />
      );
    },
    [metadata],
  );

  const searchFeedback = (
    <>
      {searchError && (
        <Alert severity="error">
          {t('Could not load rental comparisons. Please try again.')}
        </Alert>
      )}
      {listingsData != null &&
        !searching &&
        !searchError &&
        candidates.length === 0 && (
          <Alert severity="info">{t('No results found')}</Alert>
        )}
    </>
  );

  if (!complete) {
    return <ListingsStepSkeleton />;
  }

  return (
    <Stack sx={{ height: '100%' }}>
      <ListingsSelectionSection
        items={selected.map(comparable => ({
          id: String(comparable.id),
          comparable,
        }))}
        loading={generating}
        automaticDisabled={searching || generating}
        onAutomatic={() => {
          if (selected.length > 0) {
            setRefreshOpen(true);
          } else {
            void generateComparable();
          }
        }}
        onManual={() => {
          setDraft([...selected]);
          setManualOpen(true);
          setTimeout(() => {
            setSearchParams(getInitialManualSearchParams());
            setManualFiltersSeeded(true);
          }, 0);
        }}
        onReorder={({ items }) =>
          void saveSelection(items.map(item => item.comparable))
        }
        renderItem={({ item, index }) => (
          <SortableListingCard
            id={item.id}
            index={index}
            offline={metadata.get(item.comparable.id)?.offline_since != null}
            loading={removingId === item.comparable.id}
            onRemove={async () => {
              setRemovingId(item.comparable.id);
              try {
                await saveSelection(
                  selected.filter(value => value.id !== item.comparable.id),
                );
              } finally {
                setRemovingId(null);
              }
            }}
          >
            {renderCard(item.comparable)}
          </SortableListingCard>
        )}
      >
        <ComparableListingsSettings
          isPropertyComparableListingsSquareMeterCalculation
          showSquareMeterCalculation={false}
          variant="rental"
          isShowFullIdxDescription={
            report?.show_full_rental_description ?? false
          }
          includeComparableListingDetails={
            report?.include_rental_comparable_listing_details ?? false
          }
          showListingCards={
            report?.show_rental_comparable_listing_cards ?? false
          }
          comparableListingsMapZoomLevel={
            report?.rental_comparable_listings_map_zoom_level ?? null
          }
        />
        {selectedError && (
          <Alert severity="error">
            {t('Could not load rental comparisons. Please try again.')}
          </Alert>
        )}
        {missingSurface && (
          <Alert severity="info">
            {t(
              'Add a surface area to the property to compute the estimated rent.',
            )}
          </Alert>
        )}
        {searchFeedback}
      </ListingsSelectionSection>
      <FooterActionsComponentWithoutForm
        {...props}
        updating={saving || savingSearchParams || generating}
      />
      <ConfirmationDialog
        open={refreshOpen}
        onClose={() => setRefreshOpen(false)}
        onConfirm={generateComparable}
        title={t('Refresh Comparable Listings')}
        message={t('Refresh Comparable Listings Confirmation')}
      />
      <Dialog
        open={manualOpen}
        fullScreen
        css={{
          '.MuiDialog-paperFullScreen': {
            maxWidth: 'max(800px, calc(100vw - 156px))',
          },
        }}
        slots={{ transition: SlideUpTransition }}
        onClose={() => {
          if (!saving) {
            closeManualSelection();
          }
        }}
      >
        {manualOpen && property && (
          <ListingsPicker
            onClose={() => {
              if (!saving) {
                closeManualSelection();
              }
            }}
            tab={tab}
            onTabChange={nextTab => {
              setTab(nextTab);
              if (nextTab === 'automatic') {
                setCohortsRequested(true);
              }
            }}
            selectionCount={draft.length}
            onClear={() => {
              if (!saving) {
                setDraft([]);
              }
            }}
            saving={saving || savingSearchParams}
            onSave={() =>
              void saveSelection(draft).then(saved => {
                if (saved) {
                  void saveManualSearchParams().finally(closeManualSelection);
                }
              })
            }
          >
            {tab === 'manual' ? (
              !manualFiltersSeeded ? (
                <RaGrid
                  loading
                  renderSkeleton={() => <ComparableListingsSkeleton />}
                  renderItemCard={() => null}
                />
              ) : (
                <RentalComparableListings
                  lat={property.lat ?? 0}
                  lng={property.lng ?? 0}
                  stateId={property.state_place?.id ?? null}
                  selection={draft}
                  disabled={saving}
                  onSelect={item =>
                    setDraft(current => toggleRentalSelection(current, item))
                  }
                  onDetails={setDetails}
                />
              )
            ) : (
              <RaGrid
                items={candidates}
                loading={searching}
                pageSize={12}
                renderSkeleton={() => <ComparableListingsSkeleton />}
                renderItemCard={item => (
                  <SelectableCard
                    selected={draft.some(value => value.id === item.id)}
                    onChange={() => {
                      if (!saving) {
                        setDraft(current =>
                          toggleRentalSelection(current, item),
                        );
                      }
                    }}
                  >
                    {renderCard(item, 'vertical')}
                  </SelectableCard>
                )}
              />
            )}
          </ListingsPicker>
        )}
      </Dialog>
      {details && (
        <DrawerContainerProvider
          value={manualOpen ? NO_DRAWER_CONTAINER : drawerContainer}
        >
          <ListingComparableDetails
            listingId={details.id}
            rentalComparable={details}
            onClose={() => setDetails(null)}
          />
        </DrawerContainerProvider>
      )}
    </Stack>
  );
};

export default RentalListings;
